import type { PropsWithChildren } from 'react';
import { memo } from 'react';

import { IsomorphicStyles } from './style-provider';

export interface HTMLProps {
  /**
   * Styles compiled on the server while rendering the app shell. These should
   * be included in the initial client hydration to avoid a DOM mismatch error.
   */
  initialStyles?: string[];
  /**
   * Indicates that the client ran into an error during hydration but is able to
   * recover. Some parts of the application may need to implement some special
   * handling of this case.
   */
  isClientRecovery?: boolean;
  /**
   * The current version of the UI application.
   */
  version?: string;
  title?: string;
  description?: string;
}

/**
 * Top-level document wrapper for the entire application.
 */
export const HTML = memo(
  ({
    children,
    initialStyles = [],
    isClientRecovery,
    version = '0.0.0',
    title,
    description,
    // eslint-disable-next-line arrow-body-style
  }: PropsWithChildren<HTMLProps>) => {
    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta httpEquiv="x-ua-compatible" content="ie=edge" />
          <title>{title}</title>
          <meta name="description" content={description} />
          <meta name="viewport" content="width=device-width, initial-scale=1" />

          <link
            rel="stylesheet"
            type="text/css"
            href={`/fonts/fonts.css?v=${version}`}
          />
          <link
            rel="apple-touch-icon"
            sizes="180x180"
            href="/apple-touch-icon.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="32x32"
            href="/favicon-32x32.png"
          />
          <link
            rel="icon"
            type="image/png"
            sizes="16x16"
            href="/favicon-16x16.png"
          />
          <link rel="manifest" href="/site.webmanifest" />
          <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
          <meta name="msapplication-TileColor" content="#da532c" />
          <meta name="theme-color" content="#ffffff" />

          <IsomorphicStyles
            styles={initialStyles}
            isClientRecovery={isClientRecovery}
          />
        </head>

        <body>
          <div id="app">
            {children}
            <div id="portalRoot" />
          </div>
          <script type="text/javascript" src="/socket.io/socket.io.js" />
          <div id="printmount" />
        </body>
      </html>
    );
  },
);
HTML.displayName = 'HTML';

export default HTML;
